import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Search,
  BarChart3,
  Settings,
  Database,
  LogOut,
  Menu,
  X,
  History,
  SquarePen,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { cn } from "../lib/utils";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isConfigured, clearSubdomain, subdomain, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: "Dashboard", icon: LayoutDashboard, path: "/" },
    { text: "Query", icon: Search, path: "/query" },
    { text: "Chat History", icon: History, path: "/chat-history" },
    { text: "Schema Editor", icon: Database, path: "/schema" },
    { text: "Usage", icon: BarChart3, path: "/usage" },
    { text: "Settings", icon: Settings, path: "/settings" },
  ];

  const drawer = (
    <div className="h-full bg-white border-r border-stroke flex flex-col">
      <div className="px-4 py-5 border-b border-stroke">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-text-secondary">Octo AI</h1>
          <Badge variant="outline" className="bg-brand-subtle border-brand-stroke text-brand text-xs">
            Beta
          </Badge>
        </div>
      </div>
      <div className="px-4 pt-4">
        <button
          onClick={() => navigate("/query", { state: { newConversation: true } })}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-brand-subtle text-brand hover:bg-brand-stroke transition-colors"
        >
          <SquarePen className="h-4 w-4" />
          New Chat
        </button>
      </div>
      <nav className="p-4 flex-1">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.text}>
                <button
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-brand-subtle text-brand"
                      : "hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.text}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="border-t border-stroke mx-4" />
      <div className="p-4 space-y-1">
        {isConfigured && (
          <button
            onClick={clearSubdomain}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Disconnect
          </button>
        )}
        <button
          onClick={() => { logout(); navigate('/login'); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </div>
  );

  if (!isConfigured) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        {children}
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 sm:hidden"
          onClick={handleDrawerToggle}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full w-[280px] bg-background z-50 transform transition-transform duration-300 sm:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {drawer}
      </div>

      {/* Desktop drawer */}
      <div className="hidden sm:block w-[280px] flex-shrink-0">{drawer}</div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-stroke px-4 py-3 sm:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="sm:hidden"
                onClick={handleDrawerToggle}
              >
                {mobileOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
            {subdomain && (
              <span className="text-sm text-muted-foreground">{subdomain}</span>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-background p-6 flex flex-col">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
